import type { Point } from '../types/point'

/** Sub-samples per slice used to rasterize the uniform (disk-Minkowski-sum) offset — see
 *  `inflatedIntervalsForSlice`. */
export const MAX_OFFSET_SAMPLE_STEPS = 8

/** Sub-samples taken across a slice's OWN vertical span (independent of `offset`) — a single
 *  scanline at the slice's center misses any local width extremum (e.g. a star tip) that doesn't
 *  land exactly on that center Y, undershooting the true exclusion width there by up to half the
 *  slice height. Evenly sampling across the slice's own height instead bounds that error to a
 *  fraction of a point. See `inflatedIntervalsForSlice`. */
export const SLICE_INNER_SAMPLE_STEPS = 6

export function scanlineCrossings(polygon: Point[], y: number): number[] {
  const xs: number[] = []
  const n = polygon.length

  for (let i = 0; i < n; i++) {
    const a = polygon[i]
    const b = polygon[(i + 1) % n]

    if (a.y === b.y) continue

    const within = (a.y <= y && y < b.y) || (b.y <= y && y < a.y)

    if (!within) continue
    xs.push(a.x + ((y - a.y) / (b.y - a.y)) * (b.x - a.x))
  }

  return xs.sort((p, q) => p - q)
}

/** Even-odd fill rule: consecutive crossing pairs are occupied intervals. Correct for concave
 *  (e.g. star) polygons — a scanline through a star's arms naturally yields multiple disjoint
 *  intervals here. */
export function intervalsFromCrossings(xs: number[]): Array<[number, number]> {
  const out: Array<[number, number]> = []

  for (let i = 0; i + 1 < xs.length; i += 2) {
    out.push([xs[i], xs[i + 1]])
  }

  return out
}

export function mergeIntervals(intervals: Array<[number, number]>): Array<[number, number]> {
  if (intervals.length === 0) return []

  const sorted = [...intervals].sort((a, b) => a[0] - b[0])
  const merged: Array<[number, number]> = [sorted[0]]

  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1]
    const cur = sorted[i]

    if (cur[0] <= last[1]) {
      last[1] = Math.max(last[1], cur[1])
    } else {
      merged.push(cur)
    }
  }

  return merged
}

/**
 * Occupied x-interval(s) for one scanline slice spanning `[sliceTop, sliceBottom)`, uniformly
 * inflated by `offset` — the Euclidean disk-Minkowski-sum of the ORIGINAL polygon with a circle of
 * radius `offset`. Sampling the original polygon's crossings at several Y query points and
 * widening each raw interval by `sqrt(offset² − dy²)` (`dy` = perpendicular distance from the
 * query point to the slice's own span, 0 when the query point already falls inside it) is an exact
 * rasterization of that sum: no polygon offset/buffer algorithm (which would need reflex-corner
 * handling for concave shapes) is needed — this only ever re-queries the untouched original
 * contour.
 *
 * Query points always include an even spread across the slice's OWN height (`SLICE_INNER_SAMPLE_STEPS`
 * points, dy = 0 for all of them) — a single sample at the slice's center, as an earlier version of
 * this function used, misses any local width extremum (e.g. a star tip) that doesn't land exactly
 * on that center Y, undershooting the excluded width there by up to half the slice height (visibly:
 * text creeping a couple points past a shape's true tip at `offset === 0`, where legacy hugs it
 * exactly). When `offset > 0`, additional query points beyond the slice's own span (out to
 * `offset` on each side) extend the disk-sum the same way the previous single-center version did.
 */
export function inflatedIntervalsForSlice(
  polygon: Point[],
  sliceTop: number,
  sliceBottom: number,
  offset: number
): Array<[number, number]> {
  const queryYs: number[] = []

  for (let i = 0; i <= SLICE_INNER_SAMPLE_STEPS; i++) {
    queryYs.push(sliceTop + (i / SLICE_INNER_SAMPLE_STEPS) * (sliceBottom - sliceTop))
  }

  if (offset > 0) {
    const steps = Math.max(1, Math.min(MAX_OFFSET_SAMPLE_STEPS, Math.ceil(offset / 2)))

    for (let i = 1; i <= steps; i++) {
      const dy = (i / steps) * offset

      queryYs.push(sliceTop - dy, sliceBottom + dy)
    }
  }

  const raw: Array<[number, number]> = []

  for (const qy of queryYs) {
    const dy = qy < sliceTop ? sliceTop - qy : qy > sliceBottom ? qy - sliceBottom : 0

    if (dy > offset) continue

    const expand = offset > 0 ? Math.sqrt(Math.max(0, offset * offset - dy * dy)) : 0
    const xs = scanlineCrossings(polygon, qy)

    for (const [x0, x1] of intervalsFromCrossings(xs)) {
      raw.push([x0 - expand, x1 + expand])
    }
  }

  return mergeIntervals(raw)
}
